import Link from 'next/link'

import { Poster } from '@/app/components/atoms/Poster'
import { Price } from '@/app/components/atoms/Price'
import { PriceTag } from '@/app/components/atoms/PriceTag'
import { Sticker } from '@/app/components/atoms/Sticker'
import { Tag } from '@/app/components/atoms/Tag'
import { BazaarvoiceInlineRating } from '@/app/components/shared/BazaarvoiceInlineRating'
import { cx } from '@/app/lib/utils'
import { getRatingEnvironment } from '@/app/lib/ratings'

import { PurchaseCta } from '../PurchaseCta'
import { POSTER_FALLBACK_COLOR } from './constants'
import type { VerticalCardData } from './types'
import styles from './VerticalCard.module.scss'

interface VerticalCardProps extends VerticalCardData {
  readonly className?: string
}

/**
 * `VerticalCard` — tarjeta de listado genérica: imagen arriba (ratio cuadrado,
 * paridad con el patrón "card vertical" de referencia) y contenido debajo
 * (etiqueta, título, fecha/lugar, precio, CTA a todo el ancho). Reutilizable
 * por cualquier listado del sitio (eventos, productos…) que mapee su propia
 * entidad a `VerticalCardData`.
 *
 * Componente de presentación puro → Server Component (sin estado, efectos ni
 * manejadores). Renderizado defensivo: fecha, lugar, precio y etiqueta se
 * omiten si no hay dato.
 *
 * Clicabilidad (specs/019-estados-compra-eventos): el título enlaza a `href`
 * con un patrón "stretched link" (el enlace se extiende visualmente a toda la
 * tarjeta vía CSS, sin JS) — el resto de la card, aparte del CTA, navega
 * siempre a `href`. El CTA (`PurchaseCta`) vive por encima de ese enlace
 * extendido y puede tener su propio destino/estado deshabilitado.
 */
export function VerticalCard({
  productId,
  title,
  href,
  img,
  date,
  venue,
  priceDisplay,
  tag,
  sticker,
  ctaLabel,
  imageBadge,
  ctaHref,
  ctaDisabled,
  showCta = true,
  customText,
  className,
}: VerticalCardProps) {
  const { minRatingsThreshold, bazaarvoiceScriptUrl } = getRatingEnvironment()

  return (
    <article className={cx(styles.card, className)}>
      {/* Imagen — ratio cuadrado, ancho completo de la tarjeta. Renderizado
          defensivo: sin `img` se omite `Poster` (evita pasar `src=""` a
          `next/image`) y se deja solo el color de fondo placeholder. Con
          `imageBadge` (evento no comprable), la imagen pasa a blanco y negro
          (`.imageWrapMuted`) — la tag ya aporta contraste de sobra. */}
      <div
        className={cx(styles.imageWrap, imageBadge && styles.imageWrapMuted)}
        style={!img ? { background: POSTER_FALLBACK_COLOR } : undefined}
      >
        {img && <Poster src={img} alt="" className={styles.poster} />}

        {/* Categoría — esquina superior izquierda de la imagen. Se oculta
            cuando hay estado de compra: la imagen ya comunica el estado, y
            evita redundancia/ruido visual (paridad con la referencia). */}
        {tag && !imageBadge && (
          <Tag variant="black" className={styles.tagTopLeft}>
            {tag}
          </Tag>
        )}

        {/* Badge de descuento/promoción — esquina superior derecha de la
            imagen (paridad con la referencia). Sin rebaja ni promoción no
            renderiza nada. */}
        <PriceTag
          tag={priceDisplay?.tag ?? null}
          hasAppPromotions={priceDisplay?.hasAppPromotions}
          className={styles.tagTopRight}
        />

        {/* Tag de estado de compra superpuesta a la imagen (Cancelado,
            Agotado, No disponible, Disponible el <fecha>, texto del CMS).
            Ausente ⇒ estado por defecto, no se pinta nada. */}
        {imageBadge && (
          <div className={styles.imageBadge}>
            <Tag variant="status">{imageBadge}</Tag>
          </div>
        )}

        {/* Sticker comercial — esquina inferior izquierda de la imagen. Se
            oculta con estado de compra (igual que la categoría): un evento
            no comprable no promociona "Últimas entradas". */}
        {sticker && !imageBadge && (
          <div className={styles.stickerBottomLeft}>
            <Sticker>{sticker}</Sticker>
          </div>
        )}
      </div>

      {/* Orden del cuerpo: fecha → título → ciudad → precio → CTA. La posición
          real la fija `grid-template-areas` en el SCSS; el orden del DOM se
          mantiene alineado con el visual a propósito, para que la lectura por
          lector de pantalla siga la misma secuencia (WCAG 2.1 AA — 1.3.2). */}
      <div className={styles.body}>
        {date && <p className={styles.date}>{date}</p>}

        {/* h2: la página consumidora no intercala ningún h2 entre su propio h1
            y estas tarjetas — usar h3 aquí saltaría un nivel (WCAG 2.1 AA — 1.3.1). */}
        {/* `href` vacío ⇒ el producto no tiene destino derivable (p. ej. llega
            sin `genres` y no se puede comprar — ver `isLinkableProduct` en
            `mapProductToCard`). Se pinta el título como texto plano en vez de un
            enlace al documento actual, que parecería funcionar y no haría nada.
            Renderizado defensivo: los listados que puedan recibir estos productos
            deberían filtrarlos antes, pero la card no debe fabricar un enlace
            muerto si no lo hacen (specs/023-productos-destacados-module). */}
        <h2 className={styles.title}>
          {href ? (
            <Link href={href} className={styles.titleLink}>
              {title}
            </Link>
          ) : (
            title
          )}
        </h2>

        {venue && <p className={styles.venue}>{venue}</p>}

        {/* Texto editorial libre, hoy solo lo rellena el módulo de canal
            `Channel::Highlight` (specs/055-canales-modulo-destacados) — el
            resto de consumidores deja `customText` sin definir y este
            párrafo no se pinta. */}
        {customText && <p className={styles.customText}>{customText}</p>}

        {/* Estrella + puntuación de Bazaarvoice, siempre en cards con
            independencia de la categoría (spec.md FR-003/FR-004) — oculto de
            partida, revelado por `useBazaarvoiceRatingObserver` si el
            producto alcanza el umbral de valoraciones del entorno. */}
        <BazaarvoiceInlineRating
          productId={productId}
          threshold={minRatingsThreshold}
          scriptUrl={bazaarvoiceScriptUrl}
        />

        {priceDisplay && (
          <div className={styles.price}>
            <Price
              amount={priceDisplay.amount}
              isFree={priceDisplay.isFree}
              originalAmount={priceDisplay.originalAmount ?? undefined}
              variant={priceDisplay.originalAmount != null ? 'offer' : 'default'}
              showFrom
            />
          </div>
        )}

        {/* Dentro de `.body`: mismo padding lateral/inferior que el resto del
            contenido (título, precio…), en vez de a ancho completo de la card
            (paridad con la referencia, `.actions`). `showCta={false}` omite
            el CTA por completo — la card queda "solo enlace" (ver `types.ts`). */}
        {showCta && (
          <PurchaseCta
            href={href}
            ctaLabel={ctaLabel}
            ctaHref={ctaHref}
            ctaDisabled={ctaDisabled}
            className={styles.actions}
          />
        )}
      </div>
    </article>
  )
}
